import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { billingService } from '@/services/billing.service';
import type { MonthlyBillingStatus } from '@/types/billing.types';
import { useOrganisationsStore } from '@/store/organisations.store';

// Query Keys
export const billingKeys = {
  all: ['billing'] as const,
  dashboard: (orgUuid?: string) => ['billing', 'dashboard', orgUuid] as const,
  currentMonth: (orgUuid?: string) => ['billing', 'current-month', orgUuid] as const,
  usageHistory: (months: number) =>
    ['billing', 'usage-history', months] as const,
  invoices: (page: number, limit: number, status?: MonthlyBillingStatus, orgUuid?: string) =>
    ['billing', 'invoices', page, limit, status, orgUuid] as const,
  invoice: (uuid: string, orgUuid?: string) => ['billing', 'invoice', uuid, orgUuid] as const,
  sepaStatus: (orgUuid?: string) => ['billing', 'sepa', 'status', orgUuid] as const,
};

// Hook for billing dashboard
export const useBillingDashboard = () => {
  const orgUuid = useOrganisationsStore((s) => s.currentOrganisation?.uuid);
  return useQuery({
    queryKey: billingKeys.dashboard(orgUuid),
    queryFn: () => billingService.getDashboard(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Hook for current month usage
export const useCurrentMonthUsage = () => {
  const orgUuid = useOrganisationsStore((s) => s.currentOrganisation?.uuid);
  return useQuery({
    queryKey: billingKeys.currentMonth(orgUuid),
    queryFn: () => billingService.getCurrentMonthUsage(),
    refetchInterval: 60 * 1000, // Refresh every minute
  });
};

// Hook for usage history
export const useUsageHistory = (months: number = 6) => {
  return useQuery({
    queryKey: billingKeys.usageHistory(months),
    queryFn: () => billingService.getUsageHistory(months),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Hook for invoices list
export const useInvoices = (
  page: number = 1,
  limit: number = 10,
  status?: MonthlyBillingStatus
) => {
  const orgUuid = useOrganisationsStore((s) => s.currentOrganisation?.uuid);
  return useQuery({
    queryKey: billingKeys.invoices(page, limit, status, orgUuid),
    queryFn: () => billingService.getInvoices(page, limit, status),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Hook for invoice details
export const useInvoiceDetails = (uuid: string) => {
  const orgUuid = useOrganisationsStore((s) => s.currentOrganisation?.uuid);
  return useQuery({
    queryKey: billingKeys.invoice(uuid, orgUuid),
    queryFn: () => billingService.getInvoiceDetails(uuid),
    enabled: !!uuid,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// SEPA Hooks
export const useSepaStatus = () => {
  const orgUuid = useOrganisationsStore((s) => s.currentOrganisation?.uuid);
  return useQuery({
    queryKey: billingKeys.sepaStatus(orgUuid),
    queryFn: () => billingService.getSepaStatus(),
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateSepaSetupIntent = () => {
  return useMutation({
    mutationFn: () => billingService.createSepaSetupIntent(),
  });
};

export const useConfirmSepaSetup = () => {
  const orgUuid = useOrganisationsStore((s) => s.currentOrganisation?.uuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (setupIntentId: string) =>
      billingService.confirmSepaSetup(setupIntentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billingKeys.sepaStatus(orgUuid) });
    },
  });
};
